import Vue from "vue";
import VueRouter from "vue-router";
import StoreLocator from "../components/StoreLocator.vue";
const { isNavigationFailure, NavigationFailureType } = VueRouter;

Vue.use(VueRouter);

const vueRouterPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return vueRouterPush.call(this, location).catch((err) => {
    // Only throw non-duplicate nav errors. Duplicate nav errors are okay to be ignored.
    if (!isNavigationFailure(err, NavigationFailureType.duplicated)) {
      console.error(err.name);
    }
  });
};

const useCountryPage =
  document.getElementById("use_country_page").getAttribute("content") ===
  "True";

const operateWithoutTrailingSlash =
  document
    .getElementById("operate_without_trailing_slash")
    .getAttribute("content") === "True";

let countryPageCandidateNestedRoutes = [
  {
    name: "city",
    path: "/:stateName([a-z]{2,3})/:cityName([\\w-]+)",
    component: StoreLocator,
  },
  {
    name: "state",
    path: "/:stateName",
    component: StoreLocator,
  },
];

if (useCountryPage) {
  // When country page is enabled, prepend the :countryName
  // param to each template system route
  countryPageCandidateNestedRoutes = countryPageCandidateNestedRoutes.map(
    (route) => {
      const path = `/:countryName${route.path}`;
      return { ...route, path };
    },
  );

  // We should also add the country page to the beginning
  // of the routes array to ensure it'll also be matched
  countryPageCandidateNestedRoutes = [
    {
      name: "country",
      path: "/:countryName",
      component: StoreLocator,
    },
    ...countryPageCandidateNestedRoutes,
  ];
}

// USE_STOREFRONTS_URLS_V2 serves the product page at /product-search/<product>
// instead of /npd/<product>. An env uses one or the other, so detect from the
// loaded URL and keep the route names stable for name-based navigation.
const productPrefix = window.location.pathname.includes("/product-search")
  ? "product-search"
  : "npd";

export const routes = [
  {
    name: "locator-home",
    path: "/",
    component: StoreLocator,
    children: [],
  },
  {
    name: "info-page-locator",
    path: "/i/:slug",
    component: StoreLocator,
  },
  {
    name: "regional-npd-indexable-locator",
    path: `/${productPrefix}/:placeUrlName/:productUrlName`,
    component: StoreLocator,
  },
  {
    name: "npd-page-locator",
    path: `/${productPrefix}/:productUrlName`,
    component: StoreLocator,
  },
  {
    name: "product-detail",
    path: "/pd/:businessUrlName/:productUrlName",
    component: StoreLocator,
  },
  // Generic general page routes (city/state, +country when enabled) must come AFTER the
  // fixed-prefix routes so /npd/<product> isn't swallowed by /:stateName/:cityName
  // but before /:pageType/:businessUrlName so city pages resolve.
  ...countryPageCandidateNestedRoutes,
  {
    name: "business-url-detail-page-type",
    path: "/:pageType/:businessUrlName",
    component: StoreLocator,
  },
  // Required to render the locator on a internal url only used to preview
  // changes on the page builder
  {
    name: "preview-with-page-type-and-url-name",
    path: "/page-templates/:pageTemplateId/:pageType/:urlName/preview",
    component: StoreLocator,
  },
  {
    name: "preview-with-page-type",
    path: "/page-templates/:pageTemplateId/:pageType/preview",
    component: StoreLocator,
  },
  {
    name: "preview-without-page-type",
    path: "/page-templates/:pageTemplateId/preview",
    component: StoreLocator,
  },
  // Placing "/:country/:state/:city" route after all the other fixed segment pattern routes
  // to ensure more specific path matching takes precedence over the more generic path matching.
  // This ordering is crucial because the routing mechanism matches routes based on their order
  // of definition. A URL that could potentially match both patterns (due to having a similar
  // segment structure) needs to be evaluated against the most specific route first to prevent
  // incorrect route matching.

  // By positioning the generic "/:country/:state/:city" route last, we create a "catch-all"
  // pattern for URLs that do not match any of the more specific routes defined earlier in the
  // configuration.

  // TODO: how should we handle this catch all route?
  {
    name: "indexable-locator-home",
    path: "/:country/:state/:city",
    component: StoreLocator,
  },
];

// set base URL relative to subfolder (ex. example.com/stores/locator/)
const scriptName = document.querySelector('meta[name="script-name"]').content;

// For info pages and nearby product detail pages, show the locator in the page instead of
// under the '/locator/' path like it does in the standalone page.
const locatorStandalone = !!document.getElementById("locator-standalone");

const url = new URL(window.location);
const isV2 = url.pathname.includes("v2");

// The standalone locator is served at /locator/, or at /locator-legacy/ when enableNewLocator
// gives /locator/ to the new locator. Read the base off the served path so history mode keeps
// it instead of rewriting the URL to /locator/locator-legacy. The trailing slash is optional
// because OPERATE_WITHOUT_TRAILING_SLASH envs serve /locator-legacy (vue-router strips it from
// the base regardless).
const standaloneBase =
  url.pathname.slice(scriptName.length).match(/^\/[^/]+\/?/)?.[0] ||
  "/locator/";

const createRouter = (locatorStandalone) =>
  new VueRouter({
    mode: "history",
    // The `v2` path is utilized by the Template System feature during the development of a new page type.
    base:
      scriptName + (locatorStandalone ? standaloneBase : isV2 ? "/v2" : "/"),
    // The info page with an included vsl would jump to the top when searching, etc.
    // Keep it at last saved position for smoother ux.
    scrollBehavior: (to, from, savedPosition) =>
      locatorStandalone ? { y: 0 } : savedPosition,
    routes: routes,
  });

const router = createRouter(locatorStandalone);

// vue-router writes every URL as base + fullPath, so the home route always lands a trailing
// slash (/locator-legacy/). OPERATE_WITHOUT_TRAILING_SLASH envs serve and link the slashless
// form, so drop it once the navigation has written the URL.
if (operateWithoutTrailingSlash) {
  router.afterEach(() => {
    const { pathname, search, hash } = window.location;
    if (pathname.length > 1 && pathname.endsWith("/")) {
      window.history.replaceState(
        window.history.state,
        "",
        pathname.slice(0, -1) + search + hash,
      );
    }
  });
}

export default router;
